// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Plus } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ScheduleTimeline } from "@/components/shared/schedule-timeline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { type CaptureWindow, defaultSettings, findStaticOverlaps, fromSegments, toSegments } from "./schedule-utils";
import {
  DAY_MINUTES,
  SCHEDULE_LABELS,
  SCHEDULE_STRATEGIES,
  type ScheduleLabel,
  type ScheduleSettings,
  type ScheduleStrategy,
} from "./types";

interface ScheduleEditorProps {
  settings: ScheduleSettings;
  onChange: (settings: ScheduleSettings) => void;
  editable: boolean;
  /** Camera on/off window that anchors the derived strategies (every_hour / no_of_images). */
  window?: CaptureWindow;
  /** Store open/close window; hours outside it are shaded as "closed" on the timeline. */
  openWindow?: CaptureWindow;
  /** Optional "copy everyday schedule" affordance (exception editor only). */
  onCopyBase?: () => void;
}

/** The labels in play for a schedule (single for every_hour, per-slot otherwise). */
function labelsOf(settings: ScheduleSettings): ScheduleLabel[] {
  return settings.type === "every_hour" ? [settings.labelType] : settings.labelTypes;
}

export function ScheduleEditor({ settings, onChange, editable, window, openWindow, onCopyBase }: ScheduleEditorProps) {
  const { t } = useTranslation("back-office");
  const segments = toSegments(settings, window);
  const overlaps = settings.type === "static" ? findStaticOverlaps(settings.timeRanges) : [];

  // Remember the last value seen for each strategy so browsing types restores it instead of
  // re-seeding a default — keeps the dirty flag honest when switching back to the original type.
  const cacheRef = useRef<Partial<Record<ScheduleStrategy, ScheduleSettings>>>({});
  cacheRef.current[settings.type] = settings;

  const labels = labelsOf(settings);
  const isMixed = labels.length > 0 && labels.some((l) => l !== labels[0]);
  const uniformLabel = labels[0] ?? "model";

  function changeStrategy(strategy: ScheduleStrategy) {
    const awake = "awakeTime" in settings ? settings.awakeTime : 1;
    onChange(cacheRef.current[strategy] ?? defaultSettings(strategy, awake, window));
  }

  function setAwake(value: number) {
    if (settings.type === "strict") return;
    onChange({ ...settings, awakeTime: Math.max(1, Math.min(6, value)) });
  }

  function setNoOfImages(n: number) {
    if (settings.type !== "no_of_images") return;
    const count = Math.max(1, Math.min(144, n));
    const labelTypes = Array.from({ length: count }, (_, i) => settings.labelTypes[i] ?? "model");
    onChange({ ...settings, noOfImages: count, labelTypes });
  }

  // Overwrite every window's label (the "set all" action behind the label select).
  function setUniformLabel(label: ScheduleLabel) {
    if (settings.type === "every_hour") onChange({ ...settings, labelType: label });
    else onChange({ ...settings, labelTypes: settings.labelTypes.map(() => label) });
  }

  // Apply a per-window label edit from the timeline. every_hour has a single shared label,
  // so the changed window's label becomes that single label.
  function applySegments(next: typeof segments) {
    if (settings.type === "every_hour") {
      const changed = next.find((s, i) => s.label !== segments[i]?.label);
      onChange({ ...settings, labelType: changed?.label ?? settings.labelType });
    } else {
      onChange(fromSegments(settings.type, next, settings));
    }
  }

  function addWindow() {
    if (settings.type !== "static") return;
    const lastEnd = settings.timeRanges.reduce((max, [, e]) => Math.max(max, e), 0);
    const start = Math.min(lastEnd, DAY_MINUTES - 60);
    onChange({
      ...settings,
      timeRanges: [...settings.timeRanges, [start, Math.min(start + 60, DAY_MINUTES)]],
      labelTypes: [...settings.labelTypes, "model"],
    });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={settings.type} onValueChange={(v) => changeStrategy(v as ScheduleStrategy)} disabled={!editable}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCHEDULE_STRATEGIES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`stores.schedule.strategies.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={isMixed ? "" : uniformLabel}
          onValueChange={(v) => setUniformLabel(v as ScheduleLabel)}
          disabled={!editable}
        >
          <SelectTrigger className="h-9 w-28">
            <SelectValue placeholder={t("stores.schedule.mixedLabel")} />
          </SelectTrigger>
          <SelectContent>
            {SCHEDULE_LABELS.map((l) => (
              <SelectItem key={l} value={l}>
                {t(`stores.schedule.labels.${l}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {settings.type === "no_of_images" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{t("stores.schedule.fields.images")}</span>
            <Input
              type="number"
              min={1}
              max={144}
              value={settings.noOfImages}
              disabled={!editable}
              onChange={(e) => setNoOfImages(Number(e.target.value))}
              aria-label={t("stores.schedule.fields.images")}
              className="h-9 w-20"
            />
          </div>
        )}

        {settings.type !== "strict" && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{t("stores.schedule.fields.awakeTime")}</span>
            <Select value={String(settings.awakeTime)} onValueChange={(v) => setAwake(Number(v))} disabled={!editable}>
              <SelectTrigger className="h-9 w-16" aria-label={t("stores.schedule.fields.awakeTime")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {editable && (onCopyBase || settings.type === "static") && (
          <div className="ml-auto flex items-center gap-2">
            {onCopyBase && (
              <Button type="button" variant="outline" size="sm" onClick={onCopyBase}>
                {t("stores.schedule.copyEveryday")}
              </Button>
            )}
            {settings.type === "static" && (
              <Button type="button" variant="outline" size="sm" className="uppercase" onClick={addWindow}>
                <Plus className="h-4 w-4" />
                {t("stores.schedule.add")}
              </Button>
            )}
          </div>
        )}
      </div>

      <ScheduleTimeline
        strategy={settings.type}
        segments={segments}
        editable={editable}
        openWindow={openWindow}
        invalidIndices={overlaps}
        onChange={applySegments}
      />

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{editable && settings.type === "strict" && t("stores.schedule.addPinHint")}</span>
        <span className="tabular-nums">
          {settings.type === "no_of_images"
            ? t("stores.schedule.algorithmTimed", { count: segments.length })
            : t("stores.schedule.capturesPerDay", { count: segments.length })}
        </span>
      </div>
    </div>
  );
}
